'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Users, CalendarCheck, CreditCard, CalendarRange, LogOut, Settings, Bell, Package, Loader2, Building2, ShieldCheck, UserCog, GitBranch, ClipboardList, Mail, IdCard } from 'lucide-react';
import { usePermissions, ModuleKey } from '@/contexts/PermissionsContext';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import { logLogout } from '@/lib/services/auditService';
import { createClient } from '@/lib/supabase/client';


interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  module: ModuleKey;
  badge?: number;
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Operations',
    items: [
      { label: 'Members', href: '/member-management', icon: Users, module: 'members', badge: 3 },
      { label: 'Memberships', href: '/memberships', icon: IdCard, module: 'members' },
      { label: 'Attendance', href: '/attendance-check-ins', icon: CalendarCheck, module: 'attendance' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { label: 'Payments & Dues', href: '/payments-dues', icon: CreditCard, module: 'payments', badge: 7 },
    ],
  },
  {
    label: 'Bookings',
    items: [
      { label: 'Court Bookings', href: '/slot-court-bookings', icon: CalendarRange, module: 'bookings' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { label: 'Add-ons', href: '/add-ons', icon: Package, module: 'addons' },
    ],
  },
];

interface BottomItem {
  label: string;
  href: string;
  icon: React.ElementType;
  module?: ModuleKey;
}

const bottomItems: BottomItem[] = [
  { label: 'Announcements', href: '/announcements', icon: Bell },
  { label: 'Arena Profile', href: '/arena-profile', icon: Building2, module: 'settings' },
  { label: 'Settings', href: '/settings', icon: Settings, module: 'settings' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { canView, isOwner, staffProfile, assignedRoles, loading: permLoading } = usePermissions();
  const { signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    if (href === '/member-management') return pathname.startsWith('/member-management') || pathname.startsWith('/members/');
    return pathname.startsWith(href);
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      // Audit: logout event (before signing out so we still have session)
      try {
        const supabase = createClient();
        if (staffProfile?.arena_id && staffProfile?.id) {
          await logLogout(
            supabase,
            staffProfile.arena_id,
            staffProfile.id,
            staffProfile.full_name,
          );
        }
      } catch {
        // Audit failure must never block logout
      }

      await signOut();
      router.push('/sign-up-login');
    } catch {
      setSigningOut(false);
    }
  };

  const getInitials = (name: string) =>
    name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'AS';

  const getRoleLabel = () => {
    if (isOwner) return 'Arena Owner';
    if (assignedRoles.length > 0) return assignedRoles[0].name;
    return 'Staff';
  };

  return (
    <aside
      className={`relative flex flex-col bg-card border-r border-border h-screen sticky top-0 sidebar-transition overflow-hidden ${
        collapsed ? 'w-16 min-w-[64px]' : 'w-60 min-w-[240px]'
      }`}
    >
      {/* Logo */}
      <div className={`flex items-center h-16 border-b border-border px-3 gap-3 flex-shrink-0 ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-bold text-[15px] text-foreground tracking-tight whitespace-nowrap">
            ArenaSync
          </span>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2 space-y-4">
        {/* Dashboard — always visible */}
        <div>
          <Link
            href="/"
            className={`group relative flex items-center gap-3 rounded-lg px-2 py-2 text-[13px] font-medium transition-all duration-150 ${
              pathname === '/' ? 'bg-primary/10 text-primary' : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
            }`}
            title={collapsed ? 'Dashboard' : undefined}
          >
            <LayoutDashboard size={18} className="flex-shrink-0" />
            {!collapsed && <span className="whitespace-nowrap">Dashboard</span>}
            {collapsed && (
              <span className="pointer-events-none absolute left-full ml-2 z-50 whitespace-nowrap rounded-md bg-foreground text-background text-[11px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                Dashboard
              </span>
            )}
          </Link>
        </div>

        {permLoading ? (
          <div className="flex justify-center py-4">
            <Loader2 size={16} className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          navGroups.map((group) => {
            const visibleItems = group.items.filter((item) =>
              isOwner || canView(item.module)
            );
            if (visibleItems.length === 0) return null;
            return (
              <div key={`group-${group.label}`}>
                {!collapsed && (
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-2 mb-1">
                    {group.label}
                  </p>
                )}
                <ul className="space-y-0.5">
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    return (
                      <li key={`nav-${item.href}`}>
                        <Link
                          href={item.href}
                          className={`group relative flex items-center gap-3 rounded-lg px-2 py-2 text-[13px] font-medium transition-all duration-150 ${
                            active ? 'bg-primary/10 text-primary' : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
                          }`}
                          title={collapsed ? item.label : undefined}
                        >
                          <Icon size={18} className="flex-shrink-0" />
                          {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                          {!collapsed && item.badge !== undefined && (
                            <span className="ml-auto bg-accent text-accent-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center tabular-nums">
                              {item.badge}
                            </span>
                          )}
                          {collapsed && item.badge !== undefined && (
                            <span className="absolute top-1 right-1 bg-accent text-accent-foreground text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                              {item.badge}
                            </span>
                          )}
                          {collapsed && (
                            <span className="pointer-events-none absolute left-full ml-2 z-50 whitespace-nowrap rounded-md bg-foreground text-background text-[11px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                              {item.label}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}

        {/* Owner-only Admin section */}
        {!permLoading && isOwner && (
          <div>
            {!collapsed && (
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-2 mb-1">
                Admin
              </p>
            )}
            <ul className="space-y-0.5">
              {[
                { label: 'Roles & Access', href: '/roles-access', icon: ShieldCheck },
                { label: 'Staff Accounts', href: '/staff-accounts', icon: UserCog },
                { label: 'Staff Invitations', href: '/staff-invitations', icon: Mail },
                { label: 'Org Structure', href: '/organization-structure', icon: GitBranch },
                { label: 'Activity Log', href: '/activity-log', icon: ClipboardList },
              ].map((item) => {
                const ItemIcon = item.icon;
                const active = isActive(item.href);
                return (
                  <li key={`admin-${item.href}`}>
                    <Link
                      href={item.href}
                      className={`group relative flex items-center gap-3 rounded-lg px-2 py-2 text-[13px] font-medium transition-all duration-150 ${
                        active ? 'bg-primary/10 text-primary' : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      title={collapsed ? item.label : undefined}
                    >
                      <ItemIcon size={18} className="flex-shrink-0" />
                      {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                      {collapsed && (
                        <span className="pointer-events-none absolute left-full ml-2 z-50 whitespace-nowrap rounded-md bg-foreground text-background text-[11px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Staff with activity.view permission — show Activity Log */}
        {!permLoading && !isOwner && canView('activity') && (
          <div>
            {!collapsed && (
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-2 mb-1">
                Admin
              </p>
            )}
            <ul className="space-y-0.5">
              {[{ label: 'Activity Log', href: '/activity-log', icon: ClipboardList }].map((item) => {
                const ItemIcon = item.icon;
                const active = isActive(item.href);
                return (
                  <li key={`activity-nav-${item.href}`}>
                    <Link
                      href={item.href}
                      className={`group relative flex items-center gap-3 rounded-lg px-2 py-2 text-[13px] font-medium transition-all duration-150 ${
                        active ? 'bg-primary/10 text-primary' : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      title={collapsed ? item.label : undefined}
                    >
                      <ItemIcon size={18} className="flex-shrink-0" />
                      {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                      {collapsed && (
                        <span className="pointer-events-none absolute left-full ml-2 z-50 whitespace-nowrap rounded-md bg-foreground text-background text-[11px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Staff Accounts — visible to non-owner staff with canView('staff') */}
        {!permLoading && !isOwner && canView('staff') && (
          <div>
            {!collapsed && !isOwner && (
              <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground px-2 mb-1">
                Admin
              </p>
            )}
            <ul className="space-y-0.5">
              {[
                { label: 'Staff Accounts', href: '/staff-accounts', icon: UserCog },
                { label: 'Staff Invitations', href: '/staff-invitations', icon: Mail },
              ].map((item) => {
                const ItemIcon = item.icon;
                const active = isActive(item.href);
                return (
                  <li key={`staff-nav-${item.href}`}>
                    <Link
                      href={item.href}
                      className={`group relative flex items-center gap-3 rounded-lg px-2 py-2 text-[13px] font-medium transition-all duration-150 ${
                        active ? 'bg-primary/10 text-primary' : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      title={collapsed ? item.label : undefined}
                    >
                      <ItemIcon size={18} className="flex-shrink-0" />
                      {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                      {collapsed && (
                        <span className="pointer-events-none absolute left-full ml-2 z-50 whitespace-nowrap rounded-md bg-foreground text-background text-[11px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </nav>

      {/* Bottom items */}
      <div className="border-t border-border px-2 py-2 space-y-0.5">
        {bottomItems.map((item) => {
          // Hide module-restricted items
          if (item.module && !isOwner && !canView(item.module)) return null;
          const Icon = item.icon;
          const active = item.href !== '#' && isActive(item.href);
          return (
            <Link
              key={`bottom-${item.label}`}
              href={item.href}
              className={`group relative flex items-center gap-3 rounded-lg px-2 py-2 text-[13px] font-medium transition-all duration-150 ${
                active ? 'bg-primary/10 text-primary' : 'text-secondary-foreground hover:bg-muted hover:text-foreground'
              }`}
              title={collapsed ? item.label : undefined}
            >
              <Icon size={18} className="flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
              {collapsed && (
                <span className="pointer-events-none absolute left-full ml-2 z-50 whitespace-nowrap rounded-md bg-foreground text-background text-[11px] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}

        {/* User profile */}
        <div className={`flex items-center gap-2 mt-2 pt-2 border-t border-border ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <span className="text-[11px] font-bold text-primary">
              {getInitials(staffProfile?.full_name || 'AS')}
            </span>
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-foreground truncate">
                {staffProfile?.full_name || 'Arena Staff'}
              </p>
              <p className="text-[10px] text-muted-foreground truncate">{getRoleLabel()}</p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Sign out"
            >
              {signingOut ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}